// Listado de clases del día (alumno/profe/admin). Reemplaza el `/classroom` del legado.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import { listTodayClasses, type TodayClass } from '@/services/classrooms';
import styles from './ClasesHoy.module.scss';

export function ClasesHoy() {
  const [items, setItems] = useState<TodayClass[]>([]);
  const [role, setRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listTodayClasses();
      setItems(r.items);
      setRole(r.role);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <>
      <Header title="Mis clases de hoy" />
      <main className={styles.main}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>
        ) : error ? (
          <p style={{ color: '#991b1b' }}>{error}</p>
        ) : (
          <>
            <p style={{ color: '#666' }}>
              {items.length === 0 ? 'No tienes clases programadas para hoy.' : `${items.length} clase${items.length === 1 ? '' : 's'} ${role === 'teacher' ? 'que impartes' : role === 'admin' ? '(todas las del día)' : ''}`}
            </p>

            <div className={styles.list}>
              {items.length === 0 ? (
                <div className={styles.empty}>
                  <p>Cuando tengas clases programadas, aparecerán aquí.</p>
                </div>
              ) : (
                items.map((cls) => {
                  const time = new Date(cls.scheduled_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                  const stateTone = cls.classroom_state === 'open' ? 'success' : cls.classroom_state === 'closed' ? 'neutral' : 'warning';
                  const stateText = cls.classroom_state === 'open' ? 'Abierta' : cls.classroom_state === 'closed' ? 'Cerrada' : 'Programada';
                  return (
                    <div key={cls.booking_id} className={styles.row}>
                      <div className={styles.time}>{time}</div>
                      <div className={styles.info}>
                        <strong>{cls.type === 'one' ? 'Clase individual' : 'Clase grupal'} · {cls.duration_min} min</strong>
                        <span>{role === 'student' ? `Profesor: ${cls.teacher_name}` : `Profesor: ${cls.teacher_name}`}</span>
                      </div>
                      <div className={styles.badges}>
                        <Badge tone={stateTone}>{stateText}</Badge>
                        {cls.booking_status !== 'scheduled' && <Badge tone="warning">{cls.booking_status}</Badge>}
                      </div>
                      <Button asChild variant="primary" size="sm">
                        <Link to={`/aula/${cls.booking_id}`}>
                          {cls.classroom_state === 'open' ? 'Entrar' : cls.classroom_state === 'closed' ? 'Ver resumen' : 'Ir al aula'}
                        </Link>
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </main>
    </>
  );
}
